import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Play, Users, Star, MapPin } from "lucide-react";
import TheatreContactModal from "@/components/TheatreContactModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { Language } from "@/data/translations";

const IMAXTheatreSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [shouldPlay, setShouldPlay] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isTheatreModalOpen, setIsTheatreModalOpen] = useState(false);

  // Safe hook usage with fallback
  let t: (key: string) => string;
  let language: Language = "en";
  try {
    const { t: useLanguageT, language: useLanguageLang } = useLanguage();
    t = useLanguageT;
    language = useLanguageLang;
  } catch (error) {
    // Fallback to English translations if context is not available
    t = (key: string) => {
      const fallbackTranslations: Record<string, string> = {
        "cinema.title": "Private Luxury Cinema Theatre",
        "cinema.subtitle": "Your Private Cinema & Corporate Presentation Venue",
        "cinema.seatingCapacity": "Seating Capacity",
        "cinema.seatingCapacityDesc":
          "Accommodates up to 50 guests for private screenings and events",
        "cinema.corporateEvents": "Corporate Events",
        "cinema.corporateEventsDesc":
          "Perfect for MICE events, presentations, and team building activities",
        "cinema.exclusiveExperience": "Exclusive Experience",
        "cinema.exclusiveExperienceDesc":
          "The only private cinema with IMAX technology in the Bernese Oberland",
        "cinema.bookTheatre": "Book Theatre",
        "cinema.bookYourStay": "Book Your Stay",
        "cinema.locatedAt": "Located at Beatenberg Resort & Spa",
      };
      return fallbackTranslations[key] || key;
    };
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setShouldPlay(true);
            if (videoRef.current) {
              videoRef.current.play().catch(console.error);
            }
          } else {
            setShouldPlay(false);
            if (videoRef.current) {
              videoRef.current.pause();
            }
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% is visible
        rootMargin: "0px",
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate cards for mobile carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCardIndex((prevIndex) => (prevIndex + 1) % 3);
    }, 2500); // Change every 2.5 seconds

    return () => clearInterval(interval);
  }, []);

  // Safari video autoplay fix - Programmatic play trigger
  // Note: IntersectionObserver handles play/pause based on visibility
  // This ensures Safari can autoplay by attempting play() when metadata loads
  // IntersectionObserver will manage actual playback based on visibility
  useEffect(() => {
    const playVideo = async () => {
      if (!videoRef.current) return;

      try {
        // Always try to play when metadata loads (helps Safari autoplay policy)
        // IntersectionObserver will pause if not visible, which may cause AbortError (expected)
        await videoRef.current.play();
      } catch (error) {
        // Ignore AbortError - IntersectionObserver paused it (expected behavior)
        // Also ignore NotAllowedError - Safari's autoplay policy (IntersectionObserver will handle it)
        if (
          error instanceof DOMException &&
          error.name !== "AbortError" &&
          error.name !== "NotAllowedError"
        ) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try when video metadata is loaded (important for Safari autoplay policy)
    const video = videoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", playVideo);
      video.addEventListener("canplay", playVideo);
      return () => {
        video.removeEventListener("loadedmetadata", playVideo);
        video.removeEventListener("canplay", playVideo);
      };
    }
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative h-[120vh] overflow-hidden"
      data-oid="6t8_am."
    >
      {/* Full-screen background video */}
      <div className="absolute inset-0 w-full h-full" data-oid="mp4sar7">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          data-oid="w4qkk:."
        >
          <source
            src="/videos/imax-optimized-720p-vbr.mp4"
            type="video/mp4"
            data-oid="h4puyqu"
          />
        </video>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" data-oid="xon2doq"></div>
      </div>

      {/* Top white to black gradient */}
      <div
        className="absolute top-0 left-0 right-0 h-16 z-20"
        style={{
          background:
            "linear-gradient(to bottom, white 0%, white 30%, rgba(0, 0, 0, 0) 100%)",
        }}
        data-oid="uvjf_vl"
      ></div>

      {/* Bottom white to black gradient */}
      <div
        className="absolute bottom-0 left-0 right-0 h-16 z-20"
        style={{
          background:
            "linear-gradient(to top, white 0%, white 30%, rgba(0, 0, 0, 0) 100%)",
        }}
        data-oid="a5ogesd"
      ></div>

      {/* Content overlay */}
      <div
        className="relative z-10 h-full flex items-center justify-center"
        data-oid="84l:90m"
      >
        <div className="text-center max-w-6xl mx-auto px-8" data-oid="15s8:5h">
          {/* Main Title */}
          <h2
            className="text-elegant-white py-2 text-center text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight font-playfair drop-shadow-lg relative uppercase"
            data-oid="1dnnfgx"
          >
            <span className="relative z-10" data-oid="wfwceol">
              {t("cinema.title")}
            </span>
            <div
              className="absolute inset-0 rounded-full blur-sm"
              style={{
                background:
                  "radial-gradient(circle, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 50%, transparent 100%)",
              }}
              data-oid="0-j2ob1"
            ></div>
          </h2>

          {/* Subtitle */}
          <p
            className="text-base sm:text-lg md:text-xl text-elegant-white font-lato max-w-4xl mx-auto leading-relaxed mt-2 px-4"
            data-oid="-7tt7:j"
          >
            {t("cinema.subtitle")}
          </p>

          {/* Features Grid - Desktop */}
          <div
            className="hidden md:grid grid-cols-3 gap-8 mt-24 max-w-5xl mx-auto px-4"
            data-oid="9gnzdj0"
          >
            {/* Private Cinema */}
            <div
              className="flex flex-col items-center space-y-4 p-6 bg-gray-900/20 rounded-2xl backdrop-blur-sm border border-gray-900/30"
              data-oid="v:cr_o7"
            >
              <div
                className="w-16 h-16 bg-alpine-gold rounded-full flex items-center justify-center"
                data-oid="4vsulg_"
              >
                <Play
                  className="w-8 h-8 text-elegant-white"
                  data-oid="0c9wu0f"
                />
              </div>
              <h3
                className="text-xl font-semibold text-elegant-white font-playfair"
                data-oid="xaydj2z"
              >
                {t("cinema.seatingCapacity")}
              </h3>
              <p
                className="text-elegant-white/90 text-center font-lato text-sm"
                data-oid="41jd2y4"
              >
                {t("cinema.seatingCapacityDesc")}
              </p>
            </div>

            {/* Corporate Events */}
            <div
              className="flex flex-col items-center space-y-4 p-6 bg-gray-900/20 rounded-2xl backdrop-blur-sm border border-gray-900/30"
              data-oid="p7m3f04"
            >
              <div
                className="w-16 h-16 bg-alpine-gold rounded-full flex items-center justify-center"
                data-oid="myazad2"
              >
                <Users
                  className="w-8 h-8 text-elegant-white"
                  data-oid="rbo_g3_"
                />
              </div>
              <h3
                className="text-xl font-semibold text-elegant-white font-playfair"
                data-oid="th_nduu"
              >
                {t("cinema.corporateEvents")}
              </h3>
              <p
                className="text-elegant-white/90 text-center font-lato text-sm"
                data-oid="lkv1i4_"
              >
                {t("cinema.corporateEventsDesc")}
              </p>
            </div>

            {/* Unique in Region */}
            <div
              className="flex flex-col items-center space-y-4 p-6 bg-gray-900/20 rounded-2xl backdrop-blur-sm border border-gray-900/30"
              data-oid="za6e044"
            >
              <div
                className="w-16 h-16 bg-alpine-gold rounded-full flex items-center justify-center"
                data-oid="xglejo."
              >
                <Star
                  className="w-8 h-8 text-elegant-white"
                  data-oid="_jlzuda"
                />
              </div>
              <h3
                className="text-xl font-semibold text-elegant-white font-playfair"
                data-oid="_le28m:"
              >
                {t("cinema.exclusiveExperience")}
              </h3>
              <p
                className="text-elegant-white/90 text-center font-lato text-sm"
                data-oid="n3q.q9q"
              >
                {t("cinema.exclusiveExperienceDesc")}
              </p>
            </div>
          </div>

          {/* Mobile Carousel */}
          <div
            className="md:hidden mt-16 max-w-sm mx-auto px-4"
            data-oid="79s4-5x"
          >
            <div
              className="relative overflow-hidden rounded-2xl"
              data-oid="e21l9yr"
            >
              <motion.div
                className="flex"
                animate={{
                  x: -currentCardIndex * 100 + "%",
                }}
                transition={{
                  duration: 0.5,
                  ease: "easeInOut",
                }}
                data-oid="2gmxaz0"
              >
                {/* Private Cinema Card */}
                <div
                  className="w-full flex-shrink-0 flex flex-col items-center space-y-3 p-6 bg-pine-green/20 backdrop-blur-sm border border-pine-green/30"
                  data-oid="no0w8_h"
                >
                  <div
                    className="w-12 h-12 bg-alpine-gold rounded-full flex items-center justify-center"
                    data-oid="s0usid4"
                  >
                    <Play
                      className="w-6 h-6 text-elegant-white"
                      data-oid="6omelt:"
                    />
                  </div>
                  <h3
                    className="text-lg font-semibold text-elegant-white font-playfair"
                    data-oid="gptn-zt"
                  >
                    {t("cinema.seatingCapacity")}
                  </h3>
                  <p
                    className="text-elegant-white/90 text-center font-lato text-sm"
                    data-oid="m1yf5:i"
                  >
                    {t("cinema.seatingCapacityDesc")}
                  </p>
                </div>

                {/* Corporate Events Card */}
                <div
                  className="w-full flex-shrink-0 flex flex-col items-center space-y-3 p-6 bg-pine-green/20 backdrop-blur-sm border border-pine-green/30"
                  data-oid="a:sdu9z"
                >
                  <div
                    className="w-12 h-12 bg-alpine-gold rounded-full flex items-center justify-center"
                    data-oid="g.ddrit"
                  >
                    <Users
                      className="w-6 h-6 text-elegant-white"
                      data-oid="ofrf0iq"
                    />
                  </div>
                  <h3
                    className="text-lg font-semibold text-elegant-white font-playfair"
                    data-oid="ga:0:3j"
                  >
                    {t("cinema.corporateEvents")}
                  </h3>
                  <p
                    className="text-elegant-white/90 text-center font-lato text-sm"
                    data-oid="w6gsm_w"
                  >
                    {t("cinema.corporateEventsDesc")}
                  </p>
                </div>

                {/* Unique in Region Card */}
                <div
                  className="w-full flex-shrink-0 flex flex-col items-center space-y-3 p-6 bg-pine-green/20 backdrop-blur-sm border border-pine-green/30"
                  data-oid="n8i3fut"
                >
                  <div
                    className="w-12 h-12 bg-alpine-gold rounded-full flex items-center justify-center"
                    data-oid="kb6fuee"
                  >
                    <Star
                      className="w-6 h-6 text-elegant-white"
                      data-oid="ux9lu5k"
                    />
                  </div>
                  <h3
                    className="text-lg font-semibold text-elegant-white font-playfair"
                    data-oid="g.slvmv"
                  >
                    {t("cinema.exclusiveExperience")}
                  </h3>
                  <p
                    className="text-elegant-white/90 text-center font-lato text-sm"
                    data-oid="sk8.v8-"
                  >
                    {t("cinema.exclusiveExperienceDesc")}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Carousel Indicators */}
            <div
              className="flex justify-center space-x-2 mt-4"
              data-oid="hjj0fnp"
            >
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentCardIndex === index
                      ? "bg-alpine-gold"
                      : "bg-elegant-white/30"
                  }`}
                  onClick={() => setCurrentCardIndex(index)}
                  data-oid="cl39lfc"
                />
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-stretch mt-8 md:mt-12 px-4 max-w-2xl mx-auto relative z-30"
            data-oid="guyrajw"
          >
            <Button
              asChild
              size="lg"
              className="flex-1 bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 relative z-30"
              trackingType="booking"
              trackingLabel={t("cinema.bookYourStay")}
              trackingLocation="IMAX Theatre Section"
              data-oid="k0gw7x."
            >
              <a
                href={getLocalizedUrl("/accommodations", language)}
                data-oid="y6mmzfn"
                className="relative z-30"
              >
                {t("cinema.bookYourStay")}
              </a>
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={() => setIsTheatreModalOpen(true)}
              trackingType="contact_modal"
              trackingLabel="Book Theatre"
              trackingLocation="homepage_hero"
              trackingData={{ modalType: "theatre" }}
              className="flex-1 border-2 border-elegant-white text-elegant-white bg-transparent hover:bg-elegant-white hover:text-gray-900 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold rounded-full transition-all duration-300"
              data-oid="z59:chf"
            >
              {t("cinema.bookTheatre")}
            </Button>
          </div>

          {/* Location Badge */}
          <div
            className="flex items-center justify-center gap-2 mt-6 md:mt-8 text-elegant-white/80 px-4"
            data-oid="cd-8xwm"
          >
            <MapPin className="w-5 h-5" data-oid="nwikn6x" />
            <a
              href="https://www.google.com/maps/place/Parkhotel+Beatenberg/@46.6976742,7.7919849,17z/data=!4m9!3m8!1s0x478fa541a569a32d:0x786359f5bdf1a5e4!5m2!4m1!1i2!8m2!3d46.6976705!4d7.7945598!16s%2Fg%2F1tkc5ln2?entry=ttu&g_ep=EgoyMDI1MTAwMS4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="font-lato hover:text-elegant-white transition-colors duration-300 underline decoration-elegant-white/50 hover:decoration-elegant-white"
              data-oid="7_kk72m"
            >
              {t("cinema.locatedAt")}
            </a>
          </div>
        </div>
      </div>

      {/* Theatre Contact Modal */}
      <TheatreContactModal
        isOpen={isTheatreModalOpen}
        onClose={() => setIsTheatreModalOpen(false)}
        data-oid="6ii-i_b"
      />
    </section>
  );
};

export default IMAXTheatreSection;
